import { useContext, useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setChatVisible } from "@/redux/reducers/chat";
import { FuelContext } from "@/providers/fuel-provider";
import { useChatTracking } from "./useChatTracking";
import isClientSide from "@/js/helpers/isClientSide";

import { CHAT_TYPES } from "@/js/constants/chat";
import { useChatTriggers } from "./triggers/useChatTriggers";

export const useChat = () => {
  const dispatch = useDispatch();
  const { chatConfig } = useContext(FuelContext);
  const { chatType, sdkVersion } = chatConfig;
  const isVisible = useSelector((state) => state.chat.visible);
  const [chatButton, setChatButton] = useState(null);

  // Connect trigger event listeners based on what triggers are configured
  const lastTriggerFired = useChatTriggers(chatConfig);

  // Logic for chat-related Make eventing
  useChatTracking({
    chatButton,
    isVisible,
    chatType,
    lastTriggerFired,
  });

  // Set chat visibility immediately for GIGA and NONE chat types
  useEffect(() => {
    if (chatType === CHAT_TYPES.GIGA) dispatch(setChatVisible(true));
    if (chatType === CHAT_TYPES.NONE) dispatch(setChatVisible(false));
  }, [chatType, dispatch]);

  // Observe DOM for #kore-chat-window element and store in state
  useEffect(() => {
    const element = document.getElementById("kore-chat-window");
    setChatButton(element || null);

    const observer = new MutationObserver(() => {
      const foundElement = document.getElementById("kore-chat-window");
      setChatButton(foundElement || null);
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  // Sync Kore chat button visibility with Redux state.
  // Redux is the sole source of truth for chat visibility.
  useEffect(() => {
    if (!chatButton) return;
    chatButton.style.display = isVisible ? "block" : "none";
  }, [chatButton, isVisible]);

  // This allows overriding the Kore SDK version via URL parameter for easy testing
  // Memoize to avoid recreating URLSearchParams on every render
  const koreSdkVersionParam = useMemo(() => {
    if (!isClientSide()) return null;
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("koreSdkVersion");
  }, []);

  // Memoize return value to prevent Chat component from re-rendering unnecessarily
  return useMemo(
    () => ({
      sdkVersion: koreSdkVersionParam || sdkVersion,
      chatType,
    }),
    [koreSdkVersionParam, sdkVersion, chatType],
  );
};
